import { RouteSegment } from '../types/surveillanceRoute';
import { comparePk, pkMinMax, type PkLike } from './pkCompare';

/**
 * Validación de la CORRECCIÓN de un tramo del RV — espejo del validador del
 * backend (`segment-correction.validator.ts`, spec 2026-07-31).
 *
 * 🔴 ES UN ESPEJO. Si tocas una regla aquí, TÓCALA ALLÍ. Y al revés.
 *
 * El corte por carretera se añadió al backend el 2026-08-15 y este fichero se
 * quedó atrás; se vio probando en el navegador el 2026-08-27, cuando el modal
 * bloqueaba lo que el servidor permitía. Los casos de
 * `tests/unit/surveillanceRoute/segment-correction-multi-road.spec.ts` están
 * replicados con los MISMOS números en el spec homónimo del backend.
 *
 * El backend es la AUTORIDAD: revalida siempre y su error llega traducido por
 * `showBackendErrorToast`. Este helper solo adelanta el mensaje dentro del
 * modal (error inline, antes de enviar) — por eso devuelve exactamente la
 * MISMA key i18n (`errors:backend.<CÓDIGO>`) que usaría el toast: venga de
 * donde venga, el usuario lee el mismo texto.
 *
 * Reglas (ver spec): se valida contra los tramos vecinos EN EL TIEMPO, con
 * huecos permitidos y sin comprobar solapamiento entre sentidos opuestos (la
 * vuelta de la patrulla repisa PKs por definición).
 */

const ASCENDING = ['top', 'ascending', 'up'];
const DESCENDING = ['down', 'descending', 'bottom'];

type Sense = 'asc' | 'desc' | 'unknown';

const senseOf = (direction?: string | null): Sense => {
  if (!direction) return 'unknown';
  if (ASCENDING.includes(direction)) return 'asc';
  if (DESCENDING.includes(direction)) return 'desc';
  return 'unknown';
};

/** Metros 0-9999: PK con exceso en carreteras re-estacionadas (mismo rango que el backend). */
const MAX_METER = 9999;


export type CorrectionDraft = {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
  direction: string;
  /** ISO string, igual que `RouteSegment.startDate`. */
  startDate: string;
  endDate: string;
  /** La carretera del tramo. `null`/ausente = la primera del recorrido. */
  roadId?: string | null;
  /**
   * Las carreteras DECLARADAS en el selector. Con más de una el tramo cruzó,
   * así que ocupa rango en TODAS: se comparan todas, no la primera.
   */
  roadIds?: string[] | null;
  roadStretches?: {
    roadId: string;
    pkInitKm: number;
    pkInitMeter: number;
    pkEndKm: number;
    pkEndMeter: number;
  }[] | null;
};

type Geometry = {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number | null;
  pkEndMeter: number | null;
  direction: string | null;
  start: number | null;
  end: number | null;
  /** `null` = la primera del recorrido, igual que en el backend. */
  roadId: string | null;
  /** Las declaradas, cuando el tramo cruza. Ganan a `roadId`. */
  roadIds?: string[] | null;
  roadStretches?: {
    roadId: string;
    pkInitKm: number;
    pkInitMeter: number;
    pkEndKm: number;
    pkEndMeter: number;
  }[] | null;
};

const toGeometry = (s: RouteSegment): Geometry => ({
  pkInitKm: s.pkInitKm,
  pkInitMeter: s.pkInitMeter,
  pkEndKm: s.pkEndKm ?? null,
  pkEndMeter: s.pkEndMeter ?? null,
  direction: s.direction ?? null,
  start: s.startDate ? new Date(s.startDate).getTime() : null,
  end: s.endDate ? new Date(s.endDate).getTime() : null,
  roadId: s.roadId ?? null,
  roadStretches: s.roadStretches ?? null,
});

const draftToGeometry = (draft: CorrectionDraft): Geometry => ({
  pkInitKm: draft.pkInitKm,
  pkInitMeter: draft.pkInitMeter,
  pkEndKm: draft.pkEndKm,
  pkEndMeter: draft.pkEndMeter,
  direction: draft.direction,
  start: draft.startDate ? new Date(draft.startDate).getTime() : null,
  end: draft.endDate ? new Date(draft.endDate).getTime() : null,
  roadId: draft.roadId ?? null,
  roadIds: draft.roadIds ?? null,
  roadStretches: draft.roadStretches ?? null,
});

/**
 * 🔴 Extremos ordenados con `pkMinMax`, no con `Math.min/Math.max` sobre el
 * total en metros: con un kilómetro largo por medio el total construye el rango
 * DEL REVÉS. Espejo del `span` del backend.
 */
const span = (g: Geometry): { min: PkLike; max: PkLike } | null => {
  if (g.pkEndKm == null || g.pkEndMeter == null) return null;
  const [min, max] = pkMinMax(
    { km: g.pkInitKm, meter: g.pkInitMeter },
    { km: g.pkEndKm, meter: g.pkEndMeter }
  );
  return { min, max };
};

/**
 * Qué rango ocupa el tramo en cada una de sus carreteras. Espejo exacto del
 * `roadSpansOf` del backend: sin trozos es un solo rango en su vía (o en
 * `null`, la primera del recorrido); con trozos, uno por vía recorrida.
 */
const roadSpansOf = (g: Geometry): Map<string | null, { min: PkLike; max: PkLike }> => {
  const out = new Map<string | null, { min: PkLike; max: PkLike }>();
  if (g.roadStretches && g.roadStretches.length > 0) {
    for (const stretch of g.roadStretches) {
      const [min, max] = pkMinMax(
        { km: stretch.pkInitKm, meter: stretch.pkInitMeter },
        { km: stretch.pkEndKm, meter: stretch.pkEndMeter }
      );
      const previo = out.get(stretch.roadId);
      out.set(
        stretch.roadId,
        previo
          ? {
              min: comparePk(previo.min, min) <= 0 ? previo.min : min,
              max: comparePk(previo.max, max) >= 0 ? previo.max : max,
            }
          : { min, max }
      );
    }
    return out;
  }
  const propio = span(g);
  if (!propio) return out;
  // 🔴 Con varias vías DECLARADAS el tramo ocupa rango en todas.
  //
  // Antes se miraba solo la primera del selector, y eso depende del ORDEN en
  // que se marcaron: eligiendo antes el ramal que la troncal, un solape real
  // en la troncal pasaba desapercibido. Un falso negativo, que es peor que un
  // falso positivo — el aviso existe justo para que ese registro no se guarde
  // mal.
  //
  // El reparto exacto entre las dos vías lo calcula el servidor con la
  // bifurcación; aquí basta con no perder ninguna.
  if (g.roadIds && g.roadIds.length > 0) {
    for (const roadId of g.roadIds) out.set(roadId, propio);
    return out;
  }
  out.set(g.roadId ?? null, propio);
  return out;
};

/** `true` si el par ordenado en el tiempo (earlier → later) no se solapa. */
const pairIsValid = (earlier: Geometry, later: Geometry): boolean => {
  // 🔴 Multi-carretera: dos tramos de vías distintas NO se comparan por PK.
  //
  // Son numeraciones distintas sobre trazados distintos: la C-25 y su ramal
  // C-25z se separan hasta 2,76 km compartiendo kilometraje, así que sin este
  // corte un tramo del ramal en el 235+000 y otro de la troncal en el mismo PK
  // salían como solape estando a 874 m. Medido en el navegador (2026-08-27):
  // el modal de Corregir daba `SR_SEGMENT_PK_OVERLAP_PREV` justo en el caso
  // que la feature viene a permitir.
  //
  // El backend ya lo hacía; este espejo se quedó atrás. `null` es la primera
  // carretera del recorrido, así que dos tramos antiguos se siguen comparando
  // igual que antes.
  const earlierRoads = roadSpansOf(earlier);
  const laterRoads = roadSpansOf(later);
  const compartidas = [...earlierRoads.keys()].filter((r) => laterRoads.has(r));
  if (compartidas.length === 0) return true;

  const earlierSpan = span(earlier);
  if (!earlierSpan) return true;

  const senseEarlier = senseOf(earlier.direction);
  const senseLater = senseOf(later.direction);

  // Cambio de sentido: la vuelta repisa PKs por definición.
  if (
    senseEarlier !== 'unknown' &&
    senseLater !== 'unknown' &&
    senseEarlier !== senseLater
  ) {
    return true;
  }

  const laterInit: PkLike = { km: later.pkInitKm, meter: later.pkInitMeter };
  const earlierEnd: PkLike = {
    km: earlier.pkEndKm as number,
    meter: earlier.pkEndMeter as number,
  };

  if (senseEarlier === 'asc' && senseLater === 'asc') {
    return comparePk(laterInit, earlierEnd) >= 0;
  }
  if (senseEarlier === 'desc' && senseLater === 'desc') {
    return comparePk(laterInit, earlierEnd) <= 0;
  }

  const laterSpan = span(later);
  if (!laterSpan) {
    return (
      comparePk(laterInit, earlierSpan.max) >= 0 ||
      comparePk(laterInit, earlierSpan.min) <= 0
    );
  }
  return (
    comparePk(laterSpan.min, earlierSpan.max) >= 0 ||
    comparePk(laterSpan.max, earlierSpan.min) <= 0
  );
};

/**
 * Devuelve la KEY i18n del primer error encontrado, o `null` si la corrección
 * es válida. El orden de las comprobaciones es el MISMO que en el backend para
 * que el mensaje inline coincida con el que devolvería el servidor.
 */
export const validateSegmentCorrection = (
  segments: RouteSegment[],
  targetId: string,
  draft: CorrectionDraft,
  now: number = Date.now()
): string | null => {
  const sorted = [...segments].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
  const index = sorted.findIndex((s) => s.id === targetId);
  const prev = index > 0 ? toGeometry(sorted[index - 1]) : null;
  const next =
    index >= 0 && index < sorted.length - 1
      ? toGeometry(sorted[index + 1])
      : null;
  const s = draftToGeometry(draft);

  // Rangos
  for (const [km, meter] of [
    [s.pkInitKm, s.pkInitMeter],
    [s.pkEndKm ?? 0, s.pkEndMeter ?? 0],
  ]) {
    if (km < 0) return 'errors:backend.SR_PK_KM_NEGATIVE';
    if (meter < 0 || meter > MAX_METER) {
      return 'errors:backend.SR_PK_METER_OUT_OF_RANGE';
    }
  }

  // Coherencia interna
  const sense = senseOf(s.direction);
  if (s.pkEndKm != null && s.pkEndMeter != null && sense !== 'unknown') {
    const init: PkLike = { km: s.pkInitKm, meter: s.pkInitMeter };
    const end: PkLike = { km: s.pkEndKm, meter: s.pkEndMeter };
    const coherent =
      sense === 'asc' ? comparePk(end, init) >= 0 : comparePk(end, init) <= 0;
    if (!coherent) return 'errors:backend.SR_SEGMENT_PK_DIRECTION_MISMATCH';
  }

  // Horas
  if (s.start != null && s.end != null && s.end <= s.start) {
    return 'errors:backend.SR_SEGMENT_TIME_INVALID_RANGE';
  }
  if ((s.start != null && s.start > now) || (s.end != null && s.end > now)) {
    return 'errors:backend.SR_SEGMENT_TIME_FUTURE';
  }
  if (prev?.end != null && s.start != null && s.start < prev.end) {
    return 'errors:backend.SR_SEGMENT_TIME_OVERLAP_PREV';
  }
  if (next?.start != null && s.end != null && s.end > next.start) {
    return 'errors:backend.SR_SEGMENT_TIME_OVERLAP_NEXT';
  }

  // PK contra vecinos
  if (prev && !pairIsValid(prev, s)) {
    return 'errors:backend.SR_SEGMENT_PK_OVERLAP_PREV';
  }
  if (next && !pairIsValid(s, next)) {
    return 'errors:backend.SR_SEGMENT_PK_OVERLAP_NEXT';
  }

  return null;
};
